import { ArrowUpRightIcon } from '@phosphor-icons/react'
import { Link } from 'react-router'
import { match } from 'ts-pattern'
import * as styles from './finding-hover-card.css.ts'

export type FindingSeverityLevel = 'critical' | 'high' | 'medium' | 'low' | 'unknown'

/**
 * Normalize a scanner-reported severity string (which varies by tool — "HIGH", "moderate", etc.) to
 * the five levels we render. `unknown` is the fallback for anything else, including empty.
 */
export const toFindingSeverityLevel = (severity: string | undefined): FindingSeverityLevel =>
  match((severity ?? '').trim().toLowerCase())
    .with('critical', () => 'critical' as const)
    .with('high', () => 'high' as const)
    .with('medium', 'moderate', () => 'medium' as const)
    .with('low', () => 'low' as const)
    .otherwise(() => 'unknown' as const)

export type FindingHoverCardProps = {
  findingId: string
  // App UI route to the scan detail page (e.g. `/scans/{scanId}`). Required — the chip only
  // mounts the hovercard when it has a link, so this is always present.
  link: string
  tool?: string
  rule?: string
  path?: string
  severity?: string
}

/**
 * Preview card body for a linked finding, rendered inside a Radix HoverCard.Content. Shows the
 * scanner + rule, severity tag, file path, and a link into the scan that surfaced the finding.
 */
export const FindingHoverCard: React.FC<FindingHoverCardProps> = ({ findingId, link, tool, rule, path, severity }) => {
  const level = toFindingSeverityLevel(severity)
  const heading = [tool, rule].filter(r => !!r).join(' · ') || findingId

  return (
    // Radix's HoverCard.Content carries no ARIA role, and `tooltip` is not a valid one to add — it
    // forbids the focusable "View in scan" link below. The test id gives e2e a way to scope
    // assertions to the portalled preview, whose text otherwise collides with the trigger chip's.
    <div className={styles.card} data-testid="finding-hover-card">
      <div className={styles.header}>
        <span className={styles.heading}>{heading}</span>
        <span className={styles.severityTag({ level })}>{severity ?? 'Unknown'}</span>
      </div>
      {path && <p className={styles.path}>{path}</p>}
      <Link to={link} className={styles.scanLink}>
        View in scan
        <ArrowUpRightIcon size={12} weight="bold" />
      </Link>
    </div>
  )
}
